import { logEvent } from 'firebase/analytics';
import { analytics } from '../firebase';
import { collection, addDoc, serverTimestamp, query, where, getDocs, updateDoc, increment, limit } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Analytics Service for tracking user behavior and song performance
 */
class AnalyticsService {
  /**
   * Track when a user searches for a song
   */
  trackSongSearch(searchTerm: string, source: 'youtube' | 'local', userId?: string) {
    // Firebase Analytics
    if (analytics) {
      logEvent(analytics, 'search', {
        search_term: searchTerm,
        source: source,
        user_id: userId || 'guest'
      });
    }

    // Store in Firestore for detailed analysis
    if (searchTerm.trim()) {
      addDoc(collection(db, 'analytics_searches'), {
        searchTerm: searchTerm.toLowerCase().trim(),
        source,
        userId: userId || null,
        timestamp: serverTimestamp()
      }).catch(err => console.error('Failed to log search:', err));
    }
  }

  /**
   * Track when a song is added to queue
   */
  trackSongAdded(song: {
    id: string;
    title: string;
    artist?: string;
    videoId?: string;
    source: 'youtube' | 'local';
  }, partyId: string, singerName: string, userId?: string) {
    // Firebase Analytics
    if (analytics) {
      logEvent(analytics, 'add_to_queue', {
        song_id: song.id,
        song_title: song.title,
        source: song.source,
        party_id: partyId,
        user_id: userId || 'guest'
      });
    }

    // Store detailed song performance data in Firestore
    addDoc(collection(db, 'analytics_songs'), {
      songId: song.id,
      title: song.title,
      artist: song.artist || null,
      videoId: song.videoId || null,
      source: song.source,
      partyId,
      singerName,
      userId: userId || null,
      timestamp: serverTimestamp()
    }).catch(err => console.error('Failed to log song added:', err));
  }

  /**
   * Track when a song is played/completed
   */
  trackSongPlayed(song: {
    id: string;
    title: string;
    videoId?: string;
    source: 'youtube' | 'local';
  }, partyId: string, singerName: string, completed: boolean) {
    if (analytics) {
      logEvent(analytics, 'song_played', {
        song_id: song.id,
        song_title: song.title,
        source: song.source,
        completed: completed,
        party_id: partyId
      });
    }

    // Update song play counts
    addDoc(collection(db, 'analytics_plays'), {
      songId: song.id,
      title: song.title,
      videoId: song.videoId || null,
      source: song.source,
      partyId,
      singerName,
      completed,
      timestamp: serverTimestamp()
    }).catch(err => console.error('Failed to log song played:', err));
  }

  /**
   * Report a video as poor quality or inappropriate
   */
  async reportVideo(
    videoId: string,
    title: string,
    reason: 'poor_sync' | 'wrong_lyrics' | 'not_karaoke' | 'low_quality' | 'inappropriate' | 'other',
    notes: string,
    reportedBy: string,
    userId?: string
  ) {
    try {
      // Log the report
      await addDoc(collection(db, 'video_reports'), {
        videoId,
        title,
        reason,
        notes: notes.trim(),
        reportedBy,
        userId: userId || null,
        timestamp: serverTimestamp(),
        status: 'pending' // pending, reviewed, actioned
      });

      // Track in analytics
      if (analytics) {
        logEvent(analytics, 'report_video', {
          video_id: videoId,
          reason: reason,
          user_id: userId || 'guest'
        });
      }

      // Update video quality score (increment report count)
      const videoQualityRef = collection(db, 'video_quality');
      const q = query(videoQualityRef, where('videoId', '==', videoId));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        // Create new quality record
        await addDoc(videoQualityRef, {
          videoId,
          title,
          reportCount: 1,
          reasons: { [reason]: 1 },
          lastReported: serverTimestamp()
        });
      } else {
        // Update existing record
        const docRef = snapshot.docs[0].ref;
        await updateDoc(docRef, {
          reportCount: increment(1),
          [`reasons.${reason}`]: increment(1),
          lastReported: serverTimestamp()
        });
      }

      return { success: true };
    } catch (error) {
      console.error('Failed to report video:', error);
      return { success: false, error };
    }
  }

  /**
   * Auto-mark a video as bad when playback fails in production.
   * This aggressively bumps the quality score so the video is blocked from search/playback.
   */
  async markPlaybackFailed(
    videoId: string,
    title: string,
    details: string,
    reportedBy: string = 'Auto Player',
    userId?: string
  ) {
    try {
      await addDoc(collection(db, 'video_reports'), {
        videoId,
        title,
        reason: 'low_quality',
        notes: `[AUTO_PLAYBACK_FAIL] ${details}`,
        reportedBy,
        userId: userId || null,
        autoDetected: true,
        timestamp: serverTimestamp(),
        status: 'actioned'
      });

      const videoQualityRef = collection(db, 'video_quality');
      const q = query(videoQualityRef, where('videoId', '==', videoId), limit(1));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        await addDoc(videoQualityRef, {
          videoId,
          title,
          reportCount: 3,
          reasons: { low_quality: 3 },
          autoBlocked: true,
          autoFailureCount: 1,
          lastReported: serverTimestamp()
        });
      } else {
        const docRef = snapshot.docs[0].ref;
        await updateDoc(docRef, {
          reportCount: increment(3),
          ['reasons.low_quality']: increment(3),
          autoBlocked: true,
          autoFailureCount: increment(1),
          lastReported: serverTimestamp()
        });
      }

      return { success: true };
    } catch (error) {
      console.error('Failed to auto-mark playback failure:', error);
      return { success: false, error };
    }
  }

  async isVideoBlocked(videoId: string): Promise<boolean> {
    if (!videoId) return false;
    try {
      const qualityRef = collection(db, 'video_quality');
      const q = query(qualityRef, where('videoId', '==', videoId), limit(1));
      const snapshot = await getDocs(q);
      if (snapshot.empty) return false;
      const data: any = snapshot.docs[0].data();
      return data?.autoBlocked === true || (Number(data?.reportCount || 0) >= 3);
    } catch (error) {
      console.error('Failed to check video block status:', error);
      return false;
    }
  }

  /**
   * Get popular songs (most added/played)
   */
  async getPopularSongs(limit: number = 20, source?: 'youtube' | 'local') {
    try {
      const songsRef = collection(db, 'analytics_songs');
      let q = query(songsRef);
      
      if (source) {
        q = query(songsRef, where('source', '==', source));
      }

      const snapshot = await getDocs(q);
      
      // Count occurrences
      const songCounts: Record<string, { count: number; title: string; artist?: string; videoId?: string; source: string }> = {};
      
      snapshot.forEach(doc => {
        const data = doc.data();
        const key = data.songId;
        if (!songCounts[key]) {
          songCounts[key] = {
            count: 0,
            title: data.title,
            artist: data.artist,
            videoId: data.videoId,
            source: data.source
          };
        }
        songCounts[key].count++;
      });

      // Sort and return top songs
      return Object.entries(songCounts)
        .sort((a, b) => b[1].count - a[1].count)
        .slice(0, limit)
        .map(([songId, data]) => ({ songId, ...data }));
    } catch (error) {
      console.error('Failed to get popular songs:', error);
      return [];
    }
  }

  /**
   * Get trending songs from the past week
   */
  async getTrendingSongs(limit: number = 10, daysBack: number = 7) {
    try {
      const songsRef = collection(db, 'analytics_songs');
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysBack);
      
      const snapshot = await getDocs(songsRef);
      
      // Count occurrences within date range
      const songCounts: Record<string, { count: number; title: string; artist?: string; videoId?: string; source: string }> = {};
      
      snapshot.forEach(doc => {
        const data = doc.data();
        const timestamp = data.timestamp?.toDate?.() || new Date(0);
        
        // Only count songs from the past week
        if (timestamp >= cutoffDate) {
          const key = data.songId;
          if (!songCounts[key]) {
            songCounts[key] = {
              count: 0,
              title: data.title,
              artist: data.artist,
              videoId: data.videoId,
              source: data.source
            };
          }
          songCounts[key].count++;
        }
      });

      // Sort and return top trending songs
      return Object.entries(songCounts)
        .sort((a, b) => b[1].count - a[1].count)
        .slice(0, limit)
        .map(([songId, data]) => ({ songId, ...data }));
    } catch (error) {
      console.error('Failed to get trending songs:', error);
      return [];
    }
  }

  /**
   * Get song recommendations based on what other singers performed
   * "People who sang X also sang Y"
   */
  async getRecommendations(basedOnHistory: string[], limit: number = 10) {
    try {
      if (basedOnHistory.length === 0) return [];

      const songsRef = collection(db, 'analytics_songs');
      const snapshot = await getDocs(songsRef);
      
      // Group songs by singer
      const singerSongs: Record<string, Set<string>> = {};
      const songDetails: Record<string, { title: string; artist?: string; videoId?: string; source: string }> = {};
      
      snapshot.forEach(doc => {
        const data = doc.data();
        const singerKey = data.userId || data.singerName || 'unknown';
        const songId = data.songId;
        
        if (!singerSongs[singerKey]) {
          singerSongs[singerKey] = new Set();
        }
        singerSongs[singerKey].add(songId);
        
        if (!songDetails[songId]) {
          songDetails[songId] = {
            title: data.title,
            artist: data.artist,
            videoId: data.videoId,
            source: data.source
          };
        }
      });

      // Find singers who sang songs from user's history
      const relatedSingers = new Set<string>();
      Object.entries(singerSongs).forEach(([singer, songs]) => {
        // Check if this singer sang any songs from basedOnHistory
        const hasCommonSong = basedOnHistory.some(songId => songs.has(songId));
        if (hasCommonSong) {
          relatedSingers.add(singer);
        }
      });

      // Count how often other songs appear with related singers
      const recommendationScores: Record<string, number> = {};
      relatedSingers.forEach(singer => {
        const songs = singerSongs[singer];
        songs.forEach(songId => {
          // Don't recommend songs user already sang
          if (!basedOnHistory.includes(songId)) {
            recommendationScores[songId] = (recommendationScores[songId] || 0) + 1;
          }
        });
      });

      // Sort by score and return top recommendations
      return Object.entries(recommendationScores)
        .sort((a, b) => b[1] - a[1])
        .slice(0, limit)
        .map(([songId, score]) => ({
          songId,
          ...songDetails[songId],
          recommendationScore: score
        }))
        .filter(song => song.title); // Only include songs we have details for
    } catch (error) {
      console.error('Failed to get recommendations:', error);
      return [];
    }
  }

  /**
   * Get flagged videos (reported multiple times)
   */
  async getFlaggedVideos(minReports: number = 3) {
    try {
      const qualityRef = collection(db, 'video_quality');
      const snapshot = await getDocs(qualityRef);
      
      const flagged = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter((video: any) => video.reportCount >= minReports)
        .sort((a: any, b: any) => b.reportCount - a.reportCount);

      return flagged;
    } catch (error) {
      console.error('Failed to get flagged videos:', error);
      return [];
    }
  }

  /**
   * Get quality scores for multiple videos at once
   * Returns a map of videoId -> reportCount
   */
  async getVideoQualityScores(videoIds: string[]): Promise<Record<string, number>> {
    if (videoIds.length === 0) return {};

    try {
      const qualityRef = collection(db, 'video_quality');
      const snapshot = await getDocs(qualityRef);
      
      const qualityMap: Record<string, number> = {};
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        if (videoIds.includes(data.videoId)) {
          qualityMap[data.videoId] = data.autoBlocked ? 999 : (data.reportCount || 0);
        }
      });

      return qualityMap;
    } catch (error) {
      console.error('Failed to get quality scores:', error);
      return {};
    }
  }

  /**
   * Filter and sort search results by quality
   * - Remove videos with 3+ reports
   * - Sort so videos with 0 reports come before videos with reports
   */
  async filterSearchResultsByQuality<T extends { id?: string; videoId?: string }>(results: T[]): Promise<T[]> {
    if (results.length === 0) return results;

    // Extract video IDs
    const videoIds = results.map(r => r.videoId || r.id).filter(Boolean) as string[];
    
    // Get quality scores
    const qualityScores = await this.getVideoQualityScores(videoIds);

    // Filter out videos with 3+ reports
    const filtered = results.filter(result => {
      const videoId = result.videoId || result.id;
      if (!videoId) return true;
      const reportCount = qualityScores[videoId] || 0;
      return reportCount < 3;
    });

    // Sort: 0 reports first, then by increasing report count
    return filtered.sort((a, b) => {
      const aId = a.videoId || a.id || '';
      const bId = b.videoId || b.id || '';
      const aReports = qualityScores[aId] || 0;
      const bReports = qualityScores[bId] || 0;
      return aReports - bReports;
    });
  }
}

export default new AnalyticsService();
